import { dirColor } from "@/lib/format";
import { Share2, Target, ShieldAlert, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { DemoBadge } from "@/components/DemoBadge";
import { timeAgo } from "@/lib/format";

const strengthBars = (conf) => Math.round((conf / 100) * 5);

export function SignalCard({ s, onShare, onExplain, testid }) {
  const bars = strengthBars(s.confidence);
  const tid = testid || `signal-card-${s.asset.replace(/\W/g, "-").toLowerCase()}`;
  return (
    <div data-testid={tid} className="group rounded-2xl border border-border bg-card p-5 transition-all hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-display text-lg font-bold">{s.asset}</span>
            <span className="rounded-md bg-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">{s.market}</span>
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">{s.name} · {s.timeframe}</div>
        </div>
        <div className="text-right">
          <div className={`font-display text-sm font-bold ${dirColor(s.direction)}`}>{s.direction}</div>
          {s.status && <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{s.status}</div>}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <Row label="Entry" value={`${s.entry_low} – ${s.entry_high}`} />
        <Row label="R/R" value={s.risk_reward} mono />
        <Row label="Stop Loss" value={s.stop_loss} mono danger />
        <Row label="TP1" value={s.tp1} mono up />
        <Row label="Volatility" value={s.volatility} />
        <Row label="TP2" value={s.tp2} mono up />
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Confidence Score</span>
          <span className="font-mono font-semibold">{s.confidence}/100 · {s.quality}</span>
        </div>
        <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-all" style={{ width: `${s.confidence}%` }} />
        </div>
        <div className="mt-2 flex items-center gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <span key={i} className={`h-1.5 flex-1 rounded-full ${i < bars ? "bg-accent" : "bg-secondary"}`} />
          ))}
          <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">Strength</span>
        </div>
      </div>

      {s.mtf_alignment && (
        <div className="mt-3 text-[11px] text-muted-foreground">
          Multi-timeframe alignment: <span className="font-semibold text-foreground">{s.mtf_alignment}</span>
        </div>
      )}

      <p className="mt-3 line-clamp-3 text-xs leading-relaxed text-muted-foreground">{s.explanation}</p>

      <div className="mt-3 flex items-start gap-1.5 rounded-lg bg-warning/5 p-2 text-[10px] leading-relaxed text-muted-foreground ring-1 ring-warning/20">
        <ShieldAlert className="mt-0.5 h-3 w-3 shrink-0 text-warning" />
        <span>Probabilistic analytical estimate. Not a guarantee of profit.</span>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <DemoBadge />
        <div className="flex items-center gap-2">
          <Link to={`/app/signal?asset=${encodeURIComponent(s.asset)}&timeframe=${s.timeframe}`} data-testid={`${tid}-details`} className="flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs hover:bg-secondary">
            <ArrowUpRight className="h-3.5 w-3.5" /> Details
          </Link>
          {onExplain && (
            <button data-testid={`${tid}-explain`} onClick={() => onExplain(s)} className="flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs hover:bg-secondary">
              <Target className="h-3.5 w-3.5" /> Why?
            </button>
          )}
          {onShare && (
            <button data-testid={`${tid}-share`} onClick={() => onShare(s)} className="flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs hover:bg-secondary">
              <Share2 className="h-3.5 w-3.5" /> Share
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono, up, danger }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={`${mono ? "font-mono" : ""} ${up ? "text-bullish" : ""} ${danger ? "text-bearish" : ""} font-medium`}>{value}</span>
    </div>
  );
}
